import { ref } from "vue";

const isLoaded = ref(false);

export function useAcceptJs() {
    function loadAcceptJs(sandbox = false): Promise<void> {
        return new Promise((resolve, reject) => {
            if (isLoaded.value || typeof window.Accept !== "undefined") {
                isLoaded.value = true;
                resolve();
                return;
            }

            if (document.getElementById("accept-js")) {
                const script = document.getElementById("accept-js")!;
                script.addEventListener("load", () => resolve());
                script.addEventListener("error", () =>
                    reject(new Error("Accept.js failed to load.")),
                );
                return;
            }

            const script = document.createElement("script");
            script.id = "accept-js";
            script.src = sandbox
                ? "https://jstest.authorize.net/v1/Accept.js"
                : "https://js.authorize.net/v1/Accept.js";
            script.charset = "utf-8";

            script.onload = () => {
                isLoaded.value = true;
                resolve();
            };

            script.onerror = () => {
                isLoaded.value = false;
                reject(new Error("Accept.js failed to load."));
            };

            document.head.appendChild(script);
        });
    }

    function getNonce(authData: any, cardData: any): Promise<any> {
        return new Promise((resolve, reject) => {
            if (typeof window.Accept === "undefined") {
                reject(new Error("Accept.js not loaded."));
                return;
            }

            window.Accept.dispatchData(
                { authData, cardData },
                (response: any) => {
                    if (response.messages.resultCode === "Error") {
                        reject(response.messages.message);
                    } else {
                        resolve(response.opaqueData);
                    }
                },
            );
        });
    }

    /**
     * Tokenize the card data via Accept.js.
     * Returns { dataDescriptor, dataValue } on success, or null if Accept.js isn't loaded.
     */
    async function tokenizeCardData(
        paymentGateway: any,
        cardNumber: string,
        expiration: string,
    ): Promise<{ dataDescriptor: string; dataValue: string } | null> {
        if (!isLoaded.value) {
            return null;
        }

        const opaqueData = await getNonce(
            {
                apiLoginID: paymentGateway.api_login,
                clientKey: paymentGateway.public_client_key,
            },
            {
                cardNumber: cardNumber.replace(/\s/g, ""),
                month: expiration.substring(0, 2),
                year: expiration.substring(2, 4),
            },
        );

        return {
            dataDescriptor: opaqueData.dataDescriptor,
            dataValue: opaqueData.dataValue,
        };
    }

    /**
     * Determine if the error is a card validation error from Accept.js.
     * Card validation errors (E_WC_05 through E_WC_08) indicate invalid card data
     * and should block form submission. All other errors are non-validation errors
     * (e.g. encryption failures, network issues) and should allow fallback to
     * server-side card processing.
     */
    function isCardValidationError(error: any[]): boolean {
        if (!error || !error.length) {
            return false;
        }

        const validationCodes = ["E_WC_05", "E_WC_06", "E_WC_07", "E_WC_08"];

        return validationCodes.includes(error[0].code);
    }

    function tokenizationError(error: any[]) {
        if (!error || !error.length) {
            return {
                field: "card_number",
                message:
                    "Sorry, but we cannot process your card right now. Please try again in a few minutes.",
            };
        }

        const code = error[0].code;

        if (code === "E_WC_05") {
            return {
                field: "card_number",
                message: "This is not a valid card number.",
            };
        }

        if (code === "E_WC_08") {
            return {
                field: "expiration",
                message: "This card is expired.",
            };
        }

        if (code === "E_WC_06" || code === "E_WC_07") {
            return {
                field: "expiration",
                message: "This expiration is invalid.",
            };
        }

        return {
            field: "card_number",
            message: error[0].text,
        };
    }

    return {
        isCardValidationError,
        loadAcceptJs,
        tokenizeCardData,
        tokenizationError,
    };
}
